import React from 'react'
import MuiTextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import { QRCodeSVG } from 'qrcode.react'
import Barcode from 'react-barcode'
import BaseDisplayItem, {
    BaseDisplayItemProps,
    DisplayItemBase,
} from '@/feature/DisplayItems/BaseDisplayItem'

/* ---------- Types ---------- */

type ImageType = 'code39' | 'qrcode' | 'code128' | string | null

interface ImageCodeSettings {
    imageType?: ImageType
}

interface ImageCodeItem extends DisplayItemBase {
    def_dossier_def_field?: {
        def_field?: {
            settings?: ImageCodeSettings
        }
    }
}

interface Props extends BaseDisplayItemProps<string, ImageCodeItem> {
    onKeyPress?: (
        id: string | number,
        e: React.KeyboardEvent<HTMLInputElement>
    ) => void
}

/* ---------- Component ---------- */

class ImageCodeField extends BaseDisplayItem<
    string,
    ImageCodeItem,
    HTMLInputElement
> {
    private getImageType(): ImageType {
        // prefer BaseDisplayItem.getSetting
        return (
            this.getSetting<ImageType>('imageType', { defaultValue: null }) ??
            null
        )
    }

    renderShow(): React.ReactNode {
        const value = this.state.value
        if (!value) return null

        const imageType = this.getImageType()

        switch (imageType) {
            case 'code39':
                return (
                    <Box>
                        <Barcode format="CODE39" value={value} />
                    </Box>
                )
            case 'qrcode':
                return (
                    <Box>
                        <QRCodeSVG value={value} />
                    </Box>
                )
            case 'code128':
            default:
                return (
                    <Box>
                        <Barcode format="CODE128" value={value} />
                    </Box>
                )
        }
    }

    renderEdit(): React.ReactNode {
        return (
            <MuiTextField
                size="small"
                fullWidth
                name="code"
                type="text"
                inputRef={this.innerRef}
                value={this.state.value ?? ''}
                onChange={(e) => this.updateValue(e.target.value)}
                error={this.isInvalid()}
                helperText={this.isInvalid() ? this.getErrorText() : ''}
            />
        )
    }

    renderSearch(): React.ReactNode {
        const props = this.props as Props

        return (
            <MuiTextField
                size="small"
                fullWidth
                type="text"
                value={props.value ?? ''}
                onChange={(e) =>
                    props.changeValue?.(
                        props.item.id,
                        e.target.value,
                        props.item
                    )
                }
                onKeyPress={
                    props.onKeyPress
                        ? (e) => props.onKeyPress!(props.item.id, e)
                        : undefined
                }
            />
        )
    }
}

export default ImageCodeField
